import { type CharacterClass } from '@d2/core';
import { useCallback, useEffect, useState, useSyncExternalStore, type CSSProperties, type ReactElement } from 'react';

import { type CharacterFeed } from '../game/character-feed';
import { type CharacterIntents } from '../game/world';
import { castRequestOf, castableSkills, hudViewOf, skillTreesOf, unspentSkillPointsOf } from './character-view';
import { Hud } from './hud';
import { type OverlayCommand, commandFor } from './keybindings';
import { INVITE_LABEL } from './lobby-model';
import { SkillTreePanel } from './skill-tree';

/**
 * Every panel that floats over the world, and the keyboard that opens them.
 *
 * `ARCHITECTURE.md`, on the day the HUD moved out of the canvas: *"Phaser draws
 * the isometric world; React draws every panel in the DOM… The cost is real and
 * is paid in `apps/web/src/ui/`: a DOM overlay needs a positioned container the
 * size of the canvas."* This is the inside of that container. The container
 * itself is `game-canvas.tsx`'s, because only it knows how big the canvas is.
 *
 * ## It must not take a click
 *
 * The canvas underneath is click-to-move, and this element is stretched across
 * all of it. So the root is `pointer-events: none` and stays that way: a player
 * clicking the floor near the bottom of the screen must set off walking, and a
 * root that answered pointers would stop them without a single error anywhere.
 * The skill tree opts back in for itself — it is a modal, it is *meant* to eat
 * clicks, and `skill-tree.tsx` asserts its own `auto`.
 *
 * That is why the HUD holds no control at all. A badge saying you have points to
 * spend is an affordance to open a tree, not the tree, and the thing that opens
 * the tree is the keyboard below.
 *
 * ## It draws decisions, it does not make them
 *
 * Every number the two panels take is `character-view.ts`'s, and every one of
 * those is core's — the experience curve, the level gate, the prerequisite walk,
 * the point cap. Nothing in this file compares a level with anything.
 *
 * ## The invite code is here, and this is the only place it can be
 *
 * A run's code arrives once per seat, as a message, because the SDK drops room
 * metadata on seat reservation (`@d2/schema`'s `InvitePayload`). So it exists
 * for exactly as long as the connection does — which settles where it is shown.
 * **Town cannot show it**: in town there is no run, and a code left over from
 * the last one names a room that has been disposed. A player who forgot to
 * share their code before descending is, by definition, *in the run*, and this
 * is what is in front of them.
 *
 * It is a **readout and not a control**, which is what lets it live on a layer
 * that must never take a click. There is no copy button for that reason and for
 * a better one: a code is eight symbols of Crockford base 32 precisely so it can
 * be read out loud and typed back in — the alphabet exists to survive a voice
 * channel (`@d2/schema`'s `INVITE_CODE_ALPHABET`), and a clipboard is the case
 * it was not designed for.
 *
 * ## It sends intents, and predicts nothing
 *
 * A cast and a point spend go out as `useSkill` and `spendSkillPoint`, numbered
 * off the same counter a click is (`world.ts`). Nothing is predicted: a cast's
 * whole outcome is a damage number, and `ARCHITECTURE.md` forbids predicting
 * one. The player sees the result when the next snapshot lands — a monster's
 * life band, their own mana orb — which is also why the mana orb does not tick
 * down on the key press.
 */

export interface GameOverlayProps {
  /**
   * The class whose trees are drawn, or `null` when this client does not know.
   *
   * `null` is an ordinary state and not an error: the renderer sandbox has no
   * character at all, and a page restored from a stored session renders the
   * canvas while the roster request that names the class is still in flight. A
   * skill tree is meaningless without it — there is no classless character in
   * `docs/GAME-DESIGN.md` — so the tree simply does not open until it arrives.
   */
  readonly characterClass: CharacterClass | null;
  /** What this client knows about its own character. */
  readonly feed: CharacterFeed;
  /** Where a cast and a point spend go. */
  readonly intents: CharacterIntents;
  /** The body a hotkey aims at, at one reading of the browser's clock. */
  readonly nearestTarget: (localTimeMs: number) => string | null;
  /**
   * The code this run is invited by, or `null` because there is no run to
   * invite anybody to — the renderer sandbox, and the moment before the room's
   * message lands.
   *
   * Drawn as it arrived. Nothing here parses or reformats it: it is the
   * server's own eight symbols, and a client that prettified them would be
   * showing a player something to read out that is not quite what the server
   * will accept back.
   */
  readonly invite?: string | null;
}

/**
 * The layer itself: the size of its positioned parent, and transparent to a
 * pointer.
 *
 * `inset: 0` rather than a width and a height, so it is the canvas's size by
 * construction and there is no second copy of 960×540 to fall out of step with
 * the first.
 */
const LAYER: CSSProperties = {
  position: 'absolute',
  inset: 0,
  // Load-bearing. See the header, and `game-overlay.test.ts`.
  pointerEvents: 'none',
};

/**
 * The invite plate: top left, out of the way of the orbs at the bottom and of
 * the skill tree that opens in the middle.
 *
 * `pointerEvents: 'none'` of its own as well as the layer's, because a
 * descendant may not quietly opt back in — the skill tree does, on purpose,
 * and it is a modal.
 */
const PLATE: CSSProperties = {
  position: 'absolute',
  top: '0.75rem',
  left: '0.75rem',
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.5rem',
  padding: '0.3rem 0.6rem',
  border: '1px solid #3a3730',
  borderRadius: 3,
  background: 'rgba(11, 11, 13, 0.72)',
  color: '#d8d2c4',
  font: 'inherit',
  fontSize: '0.75rem',
  pointerEvents: 'none',
};

const PLATE_LABEL: CSSProperties = { color: '#6f6a5e', letterSpacing: '0.15em', textTransform: 'uppercase' };
const PLATE_CODE: CSSProperties = { color: '#c8a44c', letterSpacing: '0.25em', fontVariantNumeric: 'tabular-nums' };

export const GameOverlay = ({
  characterClass,
  feed,
  intents,
  nearestTarget,
  invite = null,
}: GameOverlayProps): ReactElement => {
  // The feed is the store and React is the subscriber. `read` hands back the
  // same object until something actually changes, which is what stops a room
  // patching at 20 Hz from re-rendering the HUD twenty times a second.
  const facts = useSyncExternalStore(feed.subscribe, feed.read);
  const [skillsOpen, setSkillsOpen] = useState(false);

  const closeSkills = useCallback((): void => {
    setSkillsOpen(false);
  }, []);

  const allocate = useCallback(
    (skillId: string): void => {
      intents.spendSkillPoint(skillId);
    },
    [intents],
  );

  useEffect(() => {
    /**
     * What a command does. Split out from the listener so the listener is the
     * three lines it should be — read the key, act, swallow it — and so the
     * decision about *which* key is `keybindings.ts`'s alone.
     */
    const run = (command: OverlayCommand): void => {
      if (command.kind === 'close-panels') {
        setSkillsOpen(false);
        return;
      }

      if (command.kind === 'toggle-skills') {
        // Nothing to draw without a class, so nothing to open. Silently, because
        // it is a transient state on a page that has just been restored rather
        // than a mistake anybody made.
        if (characterClass === null) return;

        setSkillsOpen((open) => !open);
        return;
      }

      // A player reading a skill tree is not fighting. The panel makes the same
      // call for a keystroke that lands inside it — *"quaffing a potion while
      // reading a skill tree is not something a player asked for"* — and this is
      // the same rule for one that never got that far.
      if (skillsOpen || characterClass === null) return;

      const skill = castableSkills(characterClass)[command.slot];
      if (skill === undefined) return;

      const request = castRequestOf(skill, nearestTarget(performance.now()));
      if (request === null) return;

      intents.useSkill(request.skill, request.target);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      const command = commandFor(event);
      if (command === null) return;

      // Only for a key this game actually claims. `F1` is the browser's help in
      // most of them, and a bound key that opened a help tab mid-fight would be
      // worse than no binding at all.
      event.preventDefault();
      run(command);
    };

    window.addEventListener('keydown', onKeyDown);

    return (): void => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [characterClass, intents, nearestTarget, skillsOpen]);

  const hud = hudViewOf(facts);

  return (
    <div data-testid="game-overlay" style={LAYER}>
      {/*
        A readout and never a control. See the header for why there is no copy
        button and why this cannot live in town.
      */}
      {invite !== null && (
        <p data-testid="invite-code" style={PLATE} aria-label={`${INVITE_LABEL} ${invite}`}>
          <span style={PLATE_LABEL}>{INVITE_LABEL}</span>
          {/*
            The symbols on their own as well as inside the plate, because this
            is the one value on the screen another person has to type back in —
            and `e2e/lobby.spec.ts` is that other person.
          */}
          <span data-testid="invite-code-value" style={PLATE_CODE}>
            {invite}
          </span>
        </p>
      )}

      {hud !== null && <Hud vitals={hud.vitals} progress={hud.progress} />}

      {skillsOpen && characterClass !== null && (
        <SkillTreePanel
          trees={skillTreesOf(characterClass, facts)}
          unspentSkillPoints={unspentSkillPointsOf(facts)}
          onAllocate={allocate}
          onClose={closeSkills}
        />
      )}
    </div>
  );
};
